import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useProductService } from 'services/products';
import { Product } from 'types/product';
import Button from 'components/Button';
import { RotateCcw } from 'lucide-react';
import Toast from 'components/Toast';
import styles from './ProductHistoryModal.module.scss';

interface ProductHistoryModalProps {
  show: boolean;
  onHide: () => void;
  productId: string | null;
  onRollbackSuccess?: () => void;
  onError: (message: string) => void;
}

function ProductHistoryModal({
  show,
  onHide,
  productId,
  onRollbackSuccess,
  onError,
}: ProductHistoryModalProps) {
  const productService = useProductService();
  const [productHistory, setProductHistory] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isRollbacking, setIsRollbacking] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(
    null,
  );
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadHistory = React.useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setHasError(false);
    setErrorMessage(null);
    try {
      const history = await productService.getProductHistory(productId);
      setProductHistory(history);
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'Failed to load product history';
      setHasError(true);
      setErrorMessage(errorMsg);
      // Only call onError if it's not a "no history" error
      if (!errorMsg.toLowerCase().includes('no history')) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [productId, productService, onError]);

  useEffect(() => {
    if (show && productId) {
      void loadHistory();
    } else {
      setProductHistory([]);
      setHasError(false);
      setErrorMessage(null);
    }
  }, [show, productId, loadHistory]);

  const handleRollbackClick = (snapshotId: string) => {
    setSelectedSnapshotId(snapshotId);
  };

  const handleRollbackConfirm = async () => {
    if (!selectedSnapshotId || !productId) return;

    setIsRollbacking(true);
    try {
      await productService.rollbackProduct(productId, selectedSnapshotId);

      // Refresh history
      await loadHistory();

      if (onRollbackSuccess) {
        onRollbackSuccess();
      }
      setSelectedSnapshotId(null);
      setToast({
        message: 'Product rolled back successfully',
        type: 'success',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to rollback product';
      onError(errorMessage);
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsRollbacking(false);
    }
  };

  const handleRollbackCancel = () => {
    setSelectedSnapshotId(null);
  };

  // Format currency
  const formatCurrency = (amount: string | number): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Get differences between two product versions
  const getDifferences = (
    current: Product,
    previous: Product | null,
  ): Array<{ field: string; oldValue: string; newValue: string }> => {
    if (!previous) {
      // First version, show all fields
      const diffs: Array<{
        field: string;
        oldValue: string;
        newValue: string;
      }> = [];
      if (current.name) {
        diffs.push({ field: 'Name', oldValue: '', newValue: current.name });
      }
      if (current.description) {
        diffs.push({
          field: 'Description',
          oldValue: '',
          newValue: current.description,
        });
      }
      if (current.price) {
        diffs.push({
          field: 'Price',
          oldValue: '',
          newValue: `$${formatCurrency(current.price)}`,
        });
      }
      if (current.productCategory) {
        diffs.push({
          field: 'Category',
          oldValue: '',
          newValue: current.productCategory,
        });
      }
      diffs.push({
        field: 'Track Inventory',
        oldValue: '',
        newValue: current.trackInventory ? 'Yes' : 'No',
      });
      if (current.isShippable) {
        diffs.push({
          field: 'Shippable',
          oldValue: '',
          newValue: 'Yes',
        });
      }
      return diffs;
    }

    const diffs: Array<{ field: string; oldValue: string; newValue: string }> =
      [];

    // Compare fields
    if (current.name !== previous.name) {
      diffs.push({
        field: 'Name',
        oldValue: previous.name || '',
        newValue: current.name || '',
      });
    }
    if (current.description !== previous.description) {
      diffs.push({
        field: 'Description',
        oldValue: previous.description || '',
        newValue: current.description || '',
      });
    }
    if (current.price !== previous.price) {
      diffs.push({
        field: 'Price',
        oldValue: previous.price ? `$${formatCurrency(previous.price)}` : '',
        newValue: current.price ? `$${formatCurrency(current.price)}` : '',
      });
    }
    if (current.productCategory !== previous.productCategory) {
      diffs.push({
        field: 'Category',
        oldValue: previous.productCategory || '',
        newValue: current.productCategory || '',
      });
    }
    if (current.trackInventory !== previous.trackInventory) {
      diffs.push({
        field: 'Track Inventory',
        oldValue: previous.trackInventory ? 'Yes' : 'No',
        newValue: current.trackInventory ? 'Yes' : 'No',
      });
    }
    if (current.isShippable !== previous.isShippable) {
      diffs.push({
        field: 'Shippable',
        oldValue: previous.isShippable ? 'Yes' : 'No',
        newValue: current.isShippable ? 'Yes' : 'No',
      });
    }

    return diffs;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" className={styles.modal}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            Product History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className={styles.emptyState}>Loading history...</div>
          ) : hasError || productHistory.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                {errorMessage || 'No history available for this product yet.'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void loadHistory()}
                className={styles.retryButton}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className={styles.historyList}>
              {productHistory.map((snapshot, index) => {
                const previous =
                  index < productHistory.length - 1
                    ? productHistory[index + 1]
                    : null;
                const differences = getDifferences(snapshot, previous);

                return (
                  <div key={snapshot.id} className={styles.historyItem}>
                    <div className={styles.historyItemHeader}>
                      <div>
                        <strong>{snapshot.name}</strong>
                        {index === 0 ? (
                          <span className={styles.currentBadge}>Current</span>
                        ) : (
                          <span className={styles.historyBadge}>Snapshot</span>
                        )}
                      </div>
                      <div className={styles.historyItemMeta}>
                        <span>{formatDate(snapshot.updatedAt)}</span>
                        {index > 0 && !selectedSnapshotId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRollbackClick(snapshot.id)}
                            className={styles.rollbackButton}
                          >
                            <RotateCcw size={16} />
                            Rollback
                          </Button>
                        )}
                      </div>
                    </div>
                    {selectedSnapshotId === snapshot.id && (
                      <div className={styles.rollbackConfirmation}>
                        <p>
                          Are you sure you want to rollback to the version from{' '}
                          <strong>{formatDate(snapshot.updatedAt)}</strong>?
                        </p>
                        <p className={styles.rollbackWarning}>
                          This will restore the product to its state at that
                          point in time. All changes made after this version
                          will be lost.
                        </p>
                        <div className={styles.rollbackActions}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRollbackCancel}
                            disabled={isRollbacking}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => void handleRollbackConfirm()}
                            disabled={isRollbacking}
                          >
                            {isRollbacking
                              ? 'Rolling back...'
                              : 'Confirm Rollback'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {differences.length > 0 ? (
                      <div className={styles.historyItemDetails}>
                        {differences.map((diff, diffIndex) => (
                          <div key={diffIndex} className={styles.differenceRow}>
                            <strong>{diff.field}:</strong>
                            {diff.oldValue ? (
                              <span className={styles.oldValue}>
                                {diff.oldValue}
                              </span>
                            ) : null}
                            {diff.oldValue && diff.newValue && (
                              <span className={styles.arrow}>→</span>
                            )}
                            <span className={styles.newValue}>
                              {diff.newValue}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.noChanges}>
                        No changes from previous version
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default ProductHistoryModal;
